import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardBody, Input, Label, FormGroup, Row, Col, CustomInput, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import Strings from '../../../translations/Translations';
import DatePicker from 'react-datepicker';
import PSO from '../../../services/PSO';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Context/Auth/AuthContext';
import { useHistory } from 'react-router-dom';
import Assets from '../../../services/Assets';
import Autocomplete from 'react-google-autocomplete';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const schema = Yup.object().shape({
  campaign_name: Yup.string().required(Strings('campaign_name_is_required')),
  campaign_description: Yup.string().required(Strings('campaign_description_is_required')),
  campaign_event_type: Yup.string().required(Strings('event_type_required'))
});

export default function CreateCampaignModule () {
  const history = useHistory();
  const { authenticated, toggleLoading, toggleCurrentCampaign, toggleActiveCampaign } = useAuth();
  const { register, errors, handleSubmit, reset, getValues } = useForm({
    validationSchema: schema,
  });
  const [dateStart, setDateStart] = useState();
  const [dateEnd, setDateEnd] = useState();
  const [googleAddress, setGoogleAddress] = useState();

  const [categoriesSet, setCategoriesSet] = useState();
  const [categories, setCategories] = useState();
  const [styles, setStyles] = useState();

  const [modalCancel, setModalCancel] = useState(false);
  const toggleModalCancel = useCallback(() => {
    setModalCancel(value => !value);
  }, [setModalCancel]);

  const convertDateToRequest = useCallback((date) => {
    if (date) {
      const convert = new Date(date);
      const isoString = convert.toISOString();
      
      const year = isoString.substring(0, 4);
      const month = isoString.substring(5, 7);
      const day = isoString.substring(8, 10);
      const final = `${year}-${month}-${day}--00:00`;
      return final;
    }
    return false;
  }, []);

  const getCollectionPars = useCallback(async (colid) => {
    let result = false;
    toggleLoading(true);

    try {
      const response = await PSO.getCollectionPars({
        un: authenticated.un,
        al: authenticated.al,
        colid
      });

      if (response.result === 'success' && response.its && response.its.length > 0) {
        result = response.its[0];
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
      }
    } catch (err) {
      toast.error(Strings('general_error'));
    }

    toggleLoading(false);
    return result;
  }, [authenticated, toggleLoading]);

  const onSubmit = useCallback(async (data) => {
    const createPars = {
      un: authenticated.un,
      al: authenticated.al,
      colt: 1,
      colevt: data.campaign_event_type,
      coln: data.campaign_name,
      cold: data.campaign_description,
    };

    if (data.campaign_local) {
      createPars.colloc = data.campaign_local;
    }
    if (dateStart) {
      createPars.colds = convertDateToRequest(dateStart);
    }
    if (dateEnd) {
      createPars.colde = convertDateToRequest(dateEnd);
    }
    if (data.campaign_pricing) {
      createPars.colpri = data.campaign_pricing;
    }
    if (data.campaign_tickets) {
      createPars.coltic = data.campaign_tickets;
    }
    if (data.campaign_participants) {
      createPars.colpar = data.campaign_participants;
    }
    if (data.campaign_info_url) {
      createPars.colurl = data.campaign_info_url;
    }
    if (data.campaign_custom_url) {
      createPars.colcurl = data.campaign_custom_url;
    }
    if (data.campaign_organization) {
      createPars.colorg = data.campaign_organization;
    }

    if (googleAddress) {
      if (googleAddress.formatted_address) {
        createPars.colad = googleAddress.formatted_address;
      }
      if (googleAddress.lat) {
        createPars.collati = googleAddress.lat;
      }
      if (googleAddress.lng) {
        createPars.collong = googleAddress.lng;
      }
    }

    if (data.campaign_event_style) {
      createPars.colevcat = data.campaign_event_style;
    }

    const customPars = {};
    if (data.campaign_event_style) {
      customPars.campaign_event_style = data.campaign_event_style;
    }
    customPars.campaign_surprise_me = data.campaign_surprise_me;

    toggleLoading(true);

    try {
      const response = await PSO.createCollection(createPars);

      if (response && response.result === 'success') {
        toggleLoading(false);
        
        toast.success(Strings('campaign_created'));
        reset();

        if (response.collection_id) {
          const collectionPars = await getCollectionPars(response.collection_id);
          collectionPars.customPars = customPars;

          if (collectionPars) {
            toggleCurrentCampaign(collectionPars);
            if (data.campaign_surprise_me) {
              history.replace('/campaign/create-video?create=1');
            } else {
              history.replace('/campaign');
            }
            toggleActiveCampaign(true);
          }
        }
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_request');
        toast.error(message);
        toggleLoading(false);
      }
    } catch (err) {
      toast.error(Strings('general_error'));
      toggleLoading(false);
    }
  }, [reset, authenticated, dateStart, dateEnd, getCollectionPars, history, toggleActiveCampaign, toggleCurrentCampaign, toggleLoading, convertDateToRequest, googleAddress]);

  const getCategories = useCallback(async () => {
    toggleLoading(true);
    let categories, generalCategories;

    try {
      categories = await Assets.getCategoriesSet();
    } catch (err) {
      console.error(`${Strings('error_while_getting_categories')} [ID==01]`);
    }

    try {
      generalCategories = await Assets.getCategories();
    } catch (err) {
      toast.error(`${Strings('error_while_getting_categories')} [ID==02]`);
    }

    if (categories && categories.its) {
      const filtered = categories.its.filter((item, index) => {
        return index !== 0;
      });

      // Changing 'Events' to 'Other Events'
      const newFiltered = [];
      filtered.forEach((item) => {
        if (item && item.title === 'Events') {
          item.title = 'Other events';
        }
        newFiltered.push(item);
      });
      setCategoriesSet(newFiltered);
    }
    if (generalCategories) {
      setCategories(generalCategories);
    }

    toggleLoading(false);
  }, [toggleLoading, setCategoriesSet, setCategories]);

  const setCategory = useCallback((index) => {
    if (index) {
      const target = categoriesSet[index];

      if (target && target.cats && target.cats.length > 0) {
        const cats = target.cats;
        const stylesToSet = [];

        if (categories && categories.its) {
          cats.forEach((cat) => {
            const targetCategory = categories.its.filter((value) => {
              return parseInt(value.id) === parseInt(cat);
            });
            if (targetCategory[0]) {
              stylesToSet.push(targetCategory[0]);
            }
          });
        }

        setStyles(stylesToSet);
      }
    }
  }, [categoriesSet, categories]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const toggleGoogleAddress = useCallback((place) => {
    if (place && place.formatted_address) {
      const address = {
        formatted_address: place.formatted_address,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      setGoogleAddress(address);
    }
  }, [setGoogleAddress]);

  const goToDashboard = useCallback(() => {
    history.push('/dashboard');
  }, [history]);

  const cancelCreateCampaign = useCallback(() => {
    const values = getValues();
    let filled = false;

    const fields = [
      'campaign_custom_url',
      'campaign_description',
      'campaign_info_url',
      'campaign_local',
      'campaign_name',
      'campaign_organization',
      'campaign_participants',
      'campaign_pricing',
      'campaign_tickets',
    ];

    for (let i = 0; i < fields.length; i ++) {
      if (values[fields[i]] && values[fields[i]] !== '') {
        filled = true;
      }
    }

    if (filled) {
      toggleModalCancel();
    } else {
      goToDashboard();
    }
  }, [goToDashboard, getValues, toggleModalCancel]);

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <Row>
            <Col md={12}>
              <p className='mb-0'>
                {Strings('create_campaign_intro_text')}
              </p>
            </Col>
            <Col md={12}>
              <hr />
            </Col>
          </Row>

          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_name">{Strings('name')} *</Label>
              <Input
                type="text"
                name="campaign_name"
                id="campaign_name"
                innerRef={register}
              />
              {errors.campaign_name && (
                <p className="error-validation text-left">
                  {errors.campaign_name.message}
                </p>
              )}
            </FormGroup>
            <FormGroup tag={Col} md={3}>
              <Label for="campaign_event_type">{Strings('event_type')} *</Label>
              <Input
                type="select"
                name="campaign_event_type"
                id="campaign_event_type"
                innerRef={register}
                onChange={(evt) => setCategory(evt.target.value)}
              >
                <option>{Strings('select')}</option>
                <option value={2}>Popular</option>
                <option value={4}>Business</option>
                <option value={3}>Modern</option>
                <option value={0}>Music</option>
                <option value={1}>Sports</option>
              </Input>
              {errors.campaign_event_type && (
                <p className="error-validation text-left">
                  {errors.campaign_event_type.message}
                </p>
              )}
            </FormGroup>
            <FormGroup tag={Col} md={3}>
              <Label for="campaign_event_style">{Strings('event_style')}</Label>
              <Input
                type="select"
                name="campaign_event_style"
                id="campaign_event_style"
                innerRef={register}
              >
                <option>{Strings('select')}</option>
                {styles && styles.length > 0 && styles.map((item) => (
                  <option
                    key={item.id}
                    value={item.id}
                  >
                    {item.la}
                  </option>
                ))}
              </Input>
              {errors.campaign_event_type && (
                <p className="error-validation text-left">
                  {errors.campaign_event_type.message}
                </p>
              )}
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={12}>
              <Label for="campaign_description">{Strings('description')} *</Label>
              <Input
                type="textarea"
                name="campaign_description"
                id="campaign_description"
                innerRef={register}
              />
              {errors.campaign_description && (
                <p className="error-validation text-left">
                  {errors.campaign_description.message}
                </p>
              )}
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_local">{Strings('place')}</Label>
              <Input
                type="text"
                name="campaign_local"
                id="campaign_local"
                innerRef={register}
              />
            </FormGroup>
            <FormGroup tag={Col} md={6}>
              <Label>{Strings('address')}</Label>
              <Autocomplete
                className='form-control'
                apiKey={GOOGLE_MAPS_API_KEY}
                onPlaceSelected={toggleGoogleAddress}
                options={{ types: [] }}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_pricing">{Strings('pricing')}</Label>
              <Input
                type="text"
                name="campaign_pricing"
                id="campaign_pricing"
                innerRef={register}
              />
            </FormGroup>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_tickets">{Strings('how_to_buy_a_ticket')}</Label>
              <Input
                type="text"
                name="campaign_tickets"
                id="campaign_tickets"
                innerRef={register}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={12}>
              <Label for="campaign_participants">{Strings('participants')}</Label>
              <Input
                type="text"
                name="campaign_participants"
                id="campaign_participants"
                innerRef={register}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_info_url">{Strings('info_url')}</Label>
              <Input
                type="text"
                name="campaign_info_url"
                id="campaign_info_url"
                innerRef={register}
              />
            </FormGroup>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_custom_url">{Strings('custom_url')}</Label>
              <Input
                type="text"
                name="campaign_custom_url"
                id="campaign_custom_url"
                innerRef={register}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_date_start">{Strings('date_start')}</Label>
              <DatePicker
                className="form-control"
                selected={dateStart}
                onChange={(date) => setDateStart(date)}
                minDate={new Date()}
              />
            </FormGroup>
            <FormGroup tag={Col} md={6}>
              <Label for="campaign_date_end">{Strings('date_end')}</Label>
              <DatePicker
                className="form-control"
                selected={dateEnd}
                onChange={(date) => setDateEnd(date)}
                minDate={new Date()}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={12}>
              <Label for="campaign_organization">{Strings('organized_by')}</Label>
              <Input
                type="text"
                name="campaign_organization"
                id="campaign_organization"
                innerRef={register}
              />
            </FormGroup>
          </Row>
          <Row>
            <FormGroup tag={Col} md={12}>
              <CustomInput
                type="checkbox"
                defaultChecked={true}
                id="campaign_surprise_me"
                name="campaign_surprise_me"
                label={Strings('surprise_me_text')}
                innerRef={register}
              />
            </FormGroup>
          </Row>
          <Row>
            <Col className='text-right'>
              <Button
                size='md'
                color='secondary'
                className='mt-1 mr-2 btn-wide'
                onClick={cancelCreateCampaign}
              >
                {Strings('cancel')}
              </Button>
              <Button
                size='md'
                color='success'
                className='mt-1 btn-wide'
                onClick={handleSubmit(onSubmit)}
              >
                {Strings('create')}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Modal
        isOpen={modalCancel}
        toggle={toggleModalCancel}
      >
        <ModalHeader toggle={toggleModalCancel}>
          {Strings('confirm_cancel')}
        </ModalHeader>
        <ModalBody>
          <p>
            {Strings('are_you_sure_cancel')}
          </p>
          <div className="text-right">
            <Button
              className="btn-wide mb-2 mr-2"
              size="md"
              color="secondary"
              onClick={toggleModalCancel}
            >
              {Strings('no')}
            </Button>
            <Button
              className="btn-wide mb-2 mr-2"
              size="md"
              color="danger"
              onClick={goToDashboard}
            >
              {Strings('yes_cancel')}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
